import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { devLog } from "../utils/devLog";
import {
  SUPPORTED_LANGUAGES,
  DEFAULT_LANGUAGE,
  CURRENCY_LANGUAGE_MAP,
  LANGUAGE_STORAGE_KEY
} from '../i18n/utils/constants';
import globalTranslationStore from '../utils/globalTranslationStore';
import { normalizeSessionLocale } from '../i18n/utils/locale';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(DEFAULT_LANGUAGE);
  const [isAutoSelected, setIsAutoSelected] = useState(false);
  const userLocaleOverrideRef = useRef(false);
  const activeCheckoutSessionRef = useRef(null);

  // Initialize language from localStorage or default
  useEffect(() => {
    const savedLanguage = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (savedLanguage && Object.values(SUPPORTED_LANGUAGES).includes(savedLanguage)) {
      setLanguageState(savedLanguage);
    }
  }, []);

  // Sync global translation store with current language
  useEffect(() => {
    const loadGlobalTranslations = async () => {
      try {
        devLog(`Syncing global translation store with language: ${language}`);
        await globalTranslationStore.loadLanguage(language);
        devLog('Global translation store synced successfully');
      } catch (error) {
        console.error('Failed to sync global translation store:', error);
      }
    };

    loadGlobalTranslations();
  }, [language]);

  const setLanguage = useCallback((newLanguage) => {
    if (Object.values(SUPPORTED_LANGUAGES).includes(newLanguage)) {
      setLanguageState(newLanguage);
      localStorage.setItem(LANGUAGE_STORAGE_KEY, newLanguage);
      setIsAutoSelected(false);
      userLocaleOverrideRef.current = true;
    }
  }, []);

  /**
   * Resets shopper override state when a new checkout session starts.
   * Safe to call multiple times for the same session id.
   */
  const beginCheckoutSession = useCallback((sessionId) => {
    if (!sessionId) return;
    if (activeCheckoutSessionRef.current === sessionId) return;
    activeCheckoutSessionRef.current = sessionId;
    userLocaleOverrideRef.current = false;
    setIsAutoSelected(false);
  }, []);

  /**
   * When the merchant did not set session.locale, align UI language with currency.
   */
  const autoSelectLanguageFromCurrency = useCallback((currency) => {
    if (!currency || userLocaleOverrideRef.current) return;

    const mappedLanguage = CURRENCY_LANGUAGE_MAP[currency.toUpperCase()];
    if (!mappedLanguage) return;

    if (mappedLanguage !== language) {
      setLanguageState(mappedLanguage);
      setIsAutoSelected(true);
      devLog(`Auto-selected language: ${mappedLanguage} for currency: ${currency}`);
    } else if (!isAutoSelected) {
      setIsAutoSelected(true);
    }
  }, [language, isAutoSelected]);

  const resetLanguage = useCallback(() => {
    setLanguageState(DEFAULT_LANGUAGE);
    localStorage.removeItem(LANGUAGE_STORAGE_KEY);
    setIsAutoSelected(false);
    userLocaleOverrideRef.current = false;
    activeCheckoutSessionRef.current = null;
  }, []);

  /**
   * Applies merchant session locale once. Never overrides a shopper language choice.
   */
  const applySessionLocale = useCallback((rawLocale) => {
    if (userLocaleOverrideRef.current) return;
    const normalized = normalizeSessionLocale(rawLocale);
    if (!normalized) return;
    setLanguageState(normalized);
    setIsAutoSelected(true);
  }, []);

  const getLanguageLabel = (langCode) => {
    const labels = {
      [SUPPORTED_LANGUAGES.EN]: 'English',
      [SUPPORTED_LANGUAGES.PT_BR]: 'Português (BR)'
    };
    return labels[langCode] || langCode;
  };

  const isDefaultLanguage = () => language === DEFAULT_LANGUAGE;

  const getSupportedLanguages = () => {
    return Object.values(SUPPORTED_LANGUAGES).map(lang => ({
      code: lang,
      label: getLanguageLabel(lang),
      isActive: lang === language
    }));
  };

  const value = {
    language,
    setLanguage,
    beginCheckoutSession,
    applySessionLocale,
    autoSelectLanguageFromCurrency,
    resetLanguage,
    getLanguageLabel,
    isDefaultLanguage,
    getSupportedLanguages,
    isAutoSelected,
    supportedLanguages: SUPPORTED_LANGUAGES
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
